// src/Concesionarios.tsx
import React from 'react';
import AnimationWrapper from './AnimationWrapper';

interface Concesionario {
    id: string;
    nombre: string;
    direccion: string;
    horarios: string;
    telefono: string;
    mapaUrl: string | null;
    whatsappUrl: string | null;
    imagenSede?: string;
    estado: 'activo' | 'pronto'; // Nueva propiedad para diferenciar estilos
}

const Concesionarios: React.FC = () => {
    
    // DATOS (Misma información, estructura mejorada)
    const concesionarios: Concesionario[] = [
        {
            id: 'sede-principal',
            nombre: 'Sede Principal - Caracas',
            direccion: 'Calle Sucre con Av. Guaicaipuro, Edf. Nro 60, Urb. Chacao, Municipio Chacao, Estado Miranda.',
            horarios: 'Lunes a Sábados: 8:00 AM - 5:00 PM',
            telefono: '+58 4242484359', 
            mapaUrl: 'https://maps.app.goo.gl/iDaR4P13zL6N1cqCA',
            whatsappUrl: 'https://wa.me/584143784196', 
            imagenSede: '/Consecionario-2.webp',
            estado: 'activo'
        },
    ];

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            
            {/* === 1. HERO SECTION === */}
            <div 
                className="relative h-[50vh] bg-cover bg-center flex items-center justify-center" 
                style={{ backgroundImage: `url('/Consecionario.webp')` }}
            >
                <div className="absolute inset-0 bg-black/70"></div>
                <div className="relative z-10 text-center px-4">
                    <AnimationWrapper direction="up">
                        <span className="text-yellow-500 font-bold tracking-widest uppercase text-sm mb-2 block">
                            Red Nacional
                        </span>
                        <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-4">
                            Encuéntranos
                        </h1>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            Visita nuestras sedes y vive la experiencia Bestune en persona.
                        </p>
                    </AnimationWrapper>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                
                {/* === 2. SEDES ACTIVAS (Diseño Destacado Horizontal) === */}
                <div className="mb-20">
                    <AnimationWrapper direction="up">
                        <h2 className="text-3xl font-extrabold text-black mb-10 border-l-4 border-yellow-600 pl-4">
                            Sedes Comerciales
                        </h2>
                    </AnimationWrapper>

                    {concesionarios.filter(c => c.estado === 'activo').map((sede) => (
                        <AnimationWrapper key={sede.id} direction="up">
                            <div className="bg-white shadow-xl overflow-hidden border border-gray-100 flex flex-col lg:flex-row hover:shadow-2xl transition duration-300">
                                
                                {/* Columna Imagen (Grande) */}
                                <div className="lg:w-3/5 h-64 lg:h-auto relative overflow-hidden group">
                                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition duration-500"></div>
                                    <img 
                                        src={sede.imagenSede} 
                                        alt={sede.nombre} 
                                        className="w-full h-full object-cover transform group-hover:scale-105 transition duration-700"
                                    />
                                    <div className="absolute top-4 left-4 bg-yellow-600 text-white text-xs font-bold px-3 py-1 uppercase tracking-wide shadow-md">
                                        Abierto Ahora
                                    </div>
                                </div>

                                {/* Columna Información */}
                                <div className="lg:w-2/5 p-8 lg:p-12 flex flex-col justify-center">
                                    <h3 className="text-3xl font-bold text-gray-900 mb-2">{sede.nombre}</h3>
                                    <p className="text-gray-500 text-sm mb-6 flex items-start">
                                        <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                                        {sede.direccion}
                                    </p>

                                    <div className="space-y-4 mb-8">
                                        <div className="flex items-center text-gray-700">
                                            <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                            <span className="font-medium">{sede.horarios}</span>
                                        </div>
                                        <div className="flex items-center text-gray-700">
                                            <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                                            <span className="font-medium">{sede.telefono}</span>
                                        </div>
                                    </div>

                                    {/* Botonera Premium */}
                                    <div className="flex flex-col gap-3">
                                        {/* Botón Maps */}
                                        <a href={sede.mapaUrl || '#'} target="_blank" rel="noreferrer" 
                                           className="w-full flex items-center justify-center py-3 px-4 border-2 border-yellow text-yellow-500 font-bold hover:bg-yellow-600 hover:text-white transition duration-300">
                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0121 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path></svg>
                                            Ver en Google Maps
                                        </a>

                                        {/* Botón WhatsApp */}
                                        <a href={sede.whatsappUrl || '#'} target="_blank" rel="noreferrer" 
                                           className="w-full flex items-center justify-center py-3 px-4 bg-green-50 text-green-700 font-bold border border-green-200 hover:bg-green-100 transition duration-300">
                                            <img src="/WhatsappLogo.webp" alt="WhatsApp" className="w-6 h-6 mr-2" />
                                            Chat Sede Principal
                                        </a>
                                        
                                        {/* Botón Llamar */}
                                        <a href={`tel:${sede.telefono.replace(/\s/g, '')}`} 
                                           className="w-full flex items-center justify-center py-3 px-4 bg-gray-100 text-gray-600 font-semibold hover:bg-gray-200 transition duration-300 text-sm">
                                            Llamar Directamente
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </AnimationWrapper>
                    ))}
                </div>

            </div>
        </div>
    );
};

export default Concesionarios;